import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
  Shield, 
  HardHat, 
  Stethoscope, 
  Truck, 
  Factory, 
  Briefcase, 
  Store, 
  Heart,
  Building2,
  Users,
  Landmark,
  FileText,
  CheckCircle2
} from 'lucide-react';

const specializationData = {
  'property-casualty': {
    title: 'Property & Casualty',
    subtitle: 'Small/Mid-Market',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80',
    description: 'Comprehensive property and casualty insurance solutions designed specifically for small to mid-sized businesses. We understand that every business has unique risks, and we tailor our coverage to protect what matters most to you.',
    coverageTypes: [
      'General Liability Insurance',
      'Commercial Property Insurance',
      'Business Interruption Coverage',
      'Professional Liability',
      'Product Liability Insurance',
      'Commercial Auto Insurance'
    ],
    benefits: [
      'Customized coverage packages for your specific industry',
      'Competitive rates with flexible payment options',
      'Dedicated account management',
      'Risk assessment and loss prevention services',
      'Claims advocacy and support'
    ],
    industries: [
      'Retail businesses',
      'Professional service firms',
      'Small manufacturers',
      'Contractors and tradespeople',
      'Restaurants and hospitality'
    ]
  },
  'construction-risk': {
    title: 'Construction Risk',
    subtitle: 'Solutions',
    icon: HardHat,
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80',
    description: 'Specialized insurance programs for contractors, builders, and construction professionals. From small residential projects to large commercial developments, we provide comprehensive risk management solutions.',
    coverageTypes: [
      'Builders Risk Insurance',
      'Contractors General Liability',
      'Professional Liability for Architects & Engineers',
      'Surety Bonds',
      'Equipment & Tools Coverage',
      'Workers Compensation'
    ],
    benefits: [
      'Project-specific and annual policy options',
      'Coverage for subcontractors and additional insureds',
      'Wrap-up programs for large projects',
      'Safety and risk management consulting',
      'Fast certificates of insurance'
    ],
    industries: [
      'General contractors',
      'Specialty trade contractors',
      'Residential builders',
      'Commercial construction',
      'Infrastructure projects'
    ]
  },
  'healthcare-risk': {
    title: 'Healthcare Risk',
    subtitle: 'Solutions',
    icon: Stethoscope,
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&q=80',
    description: 'Comprehensive insurance solutions for healthcare providers, from individual practitioners to large medical facilities. We navigate the complex regulatory environment to provide coverage that protects your practice and patients.',
    coverageTypes: [
      'Medical Malpractice Insurance',
      'General Liability',
      'Cyber Liability & Data Breach',
      'Directors & Officers Liability',
      'Employment Practices Liability',
      'Property & Equipment Coverage'
    ],
    benefits: [
      'Occurrence and claims-made policy options',
      'Tail coverage and prior acts coverage',
      'Risk management resources and training',
      'Regulatory compliance support',
      'Dedicated healthcare claims specialists'
    ],
    industries: [
      'Physicians and surgeons',
      'Dentists and dental practices',
      'Nursing homes and assisted living',
      'Home health agencies',
      'Medical clinics and urgent care'
    ]
  },
  'transportation-risk': {
    title: 'Transportation Risk',
    subtitle: 'Solutions',
    icon: Truck,
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1200&q=80',
    description: 'Full-service transportation insurance programs for trucking companies, logistics providers, and fleet operators. We provide coverage that keeps your business moving.',
    coverageTypes: [
      'Commercial Auto Liability',
      'Physical Damage Coverage',
      'Motor Truck Cargo',
      'Trailer Interchange',
      'Non-Trucking Liability',
      'Workers Compensation'
    ],
    benefits: [
      'Coverage for owner-operators and fleet owners',
      'Flexible radius and commodity options',
      'DOT and FMCSA compliance assistance',
      'Driver safety programs and discounts',
      '24/7 claims reporting'
    ],
    industries: [
      'Long-haul trucking',
      'Local delivery services',
      'Freight brokers',
      'Moving and storage',
      'Passenger transportation'
    ]
  },
  'manufacturing': {
    title: 'Manufacturing',
    subtitle: '& Industrial',
    icon: Factory,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&q=80',
    description: 'Tailored insurance solutions for manufacturers of all sizes. We understand the unique risks of manufacturing operations and provide comprehensive coverage to protect your facilities, equipment, and products.',
    coverageTypes: [
      'Product Liability Insurance',
      'Property & Equipment Coverage',
      'Business Interruption',
      'Environmental Liability',
      'Workers Compensation',
      'Commercial Auto'
    ],
    benefits: [
      'Coverage for product recalls',
      'Supply chain risk management',
      'Equipment breakdown protection',
      'Pollution liability options',
      'International coverage available'
    ],
    industries: [
      'Food and beverage manufacturing',
      'Metal fabrication',
      'Plastics and chemicals',
      'Electronics manufacturing',
      'Textile and apparel'
    ]
  },
  'professional-services': {
    title: 'Professional',
    subtitle: 'Services',
    icon: Briefcase,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80',
    description: 'Specialized insurance programs for professional service firms. We protect your expertise and reputation with comprehensive liability coverage tailored to your profession.',
    coverageTypes: [
      'Professional Liability (E&O)',
      'General Liability',
      'Cyber Liability',
      'Directors & Officers',
      'Employment Practices Liability',
      'Fiduciary Liability'
    ],
    benefits: [
      'Industry-specific policy forms',
      'Prior acts coverage available',
      'Disciplinary defense coverage',
      'Cyber and data breach protection',
      'Flexible deductible options'
    ],
    industries: [
      'Accountants and CPAs',
      'Attorneys and law firms',
      'Consultants and advisors',
      'IT professionals',
      'Real estate professionals'
    ]
  },
  'retail-hospitality': {
    title: 'Retail',
    subtitle: '& Hospitality',
    icon: Store,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80',
    description: 'Comprehensive coverage solutions for retail stores, restaurants, hotels, and hospitality businesses. We understand the fast-paced nature of your industry and provide protection that keeps your business running.',
    coverageTypes: [
      'Business Owners Policy (BOP)',
      'Liquor Liability',
      'Food Contamination Coverage',
      'Property & Contents',
      'Workers Compensation',
      'Umbrella Liability'
    ],
    benefits: [
      'Package policies for cost savings',
      'Coverage for spoilage and equipment breakdown',
      'Special event coverage',
      'Employee dishonesty protection',
      'Business interruption coverage'
    ],
    industries: [
      'Retail stores and shops',
      'Restaurants and cafes',
      'Hotels and motels',
      'Bars and nightclubs',
      'Event venues'
    ]
  },
  'non-profit': {
    title: 'Non-Profit',
    subtitle: 'Organizations',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&q=80',
    description: 'Specialized insurance solutions designed for non-profit organizations, charities, and foundations. We help protect your mission with affordable coverage tailored to your unique needs.',
    coverageTypes: [
      'Directors & Officers Liability',
      'General Liability',
      'Professional Liability',
      'Volunteer Accident Coverage',
      'Property Insurance',
      'Special Event Coverage'
    ],
    benefits: [
      'Affordable premiums for non-profits',
      'Coverage for volunteers and board members',
      'Abuse and molestation coverage',
      'Fundraising event protection',
      'Grant compliance support'
    ],
    industries: [
      'Charitable organizations',
      'Educational institutions',
      'Religious organizations',
      'Social service agencies',
      'Arts and cultural organizations'
    ]
  },
  'real-estate': {
    title: 'Real Estate',
    subtitle: 'Risk Solutions',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80',
    description: 'Comprehensive insurance programs for property owners, managers, and real estate professionals. From single properties to large portfolios, we provide coverage that protects your investments.',
    coverageTypes: [
      'Property Insurance',
      'General Liability',
      'Professional Liability (E&O)',
      'Flood and Earthquake',
      'Umbrella/Excess Liability',
      'Tenant Discrimination Coverage'
    ],
    benefits: [
      'Portfolio pricing for multiple properties',
      'Coverage for vacant buildings',
      'Renovation and construction coverage',
      'Loss of rents protection',
      'Environmental liability options'
    ],
    industries: [
      'Commercial property owners',
      'Residential landlords',
      'Property managers',
      'Real estate developers',
      'HOAs and condo associations'
    ]
  },
  'employee-benefits': {
    title: 'Employee',
    subtitle: 'Benefits',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
    description: 'Complete employee benefits solutions to help you attract and retain top talent. We design benefit packages that meet your budget while providing meaningful coverage for your employees.',
    coverageTypes: [
      'Group Health Insurance',
      'Dental and Vision Plans',
      'Life and Disability Insurance',
      'Retirement Plans (401k)',
      'Voluntary Benefits',
      'Health Savings Accounts (HSA)'
    ],
    benefits: [
      'Multiple carrier options for competitive pricing',
      'Compliance with ACA and ERISA',
      'Online enrollment and administration',
      'Employee communication materials',
      'Dedicated benefits consultant'
    ],
    industries: [
      'Small businesses (2-50 employees)',
      'Mid-size companies (51-500)',
      'Large employers (500+)',
      'Non-profit organizations',
      'Professional employer organizations'
    ]
  },
  'financial-institutions': {
    title: 'Financial Institutions',
    subtitle: 'Risk Solutions',
    icon: Landmark,
    image: 'https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?w=1200&q=80',
    description: 'Specialized insurance solutions for banks, credit unions, investment firms, and other financial institutions. We understand the complex regulatory environment and unique risks you face.',
    coverageTypes: [
      'Directors & Officers Liability',
      'Financial Institution Bond',
      'Professional Liability (E&O)',
      'Cyber Liability',
      'Employment Practices Liability',
      'Fiduciary Liability'
    ],
    benefits: [
      'Coverage for regulatory investigations',
      'Cyber and data breach protection',
      'Fidelity and crime coverage',
      'Securities and investment coverage',
      'Compliance consulting services'
    ],
    industries: [
      'Community banks',
      'Credit unions',
      'Investment advisors',
      'Broker-dealers',
      'Insurance agencies'
    ]
  },
  'small-business': {
    title: 'Enterprise Insurance',
    subtitle: 'for Small Business',
    icon: FileText,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
    description: 'Streamlined insurance solutions for small businesses and startups. We make it easy to get the coverage you need with competitive rates and flexible options.',
    coverageTypes: [
      'Business Owners Policy (BOP)',
      'General Liability',
      'Professional Liability',
      'Commercial Property',
      'Workers Compensation',
      'Commercial Auto'
    ],
    benefits: [
      'Quick quotes and easy applications',
      'Package policies for cost savings',
      'Pay-as-you-go workers comp options',
      'Growth-friendly coverage limits',
      'Certificate management tools'
    ],
    industries: [
      'Startups and new businesses',
      'Home-based businesses',
      'Freelancers and consultants',
      'E-commerce businesses',
      'Service providers'
    ]
  }
};

const SpecializationDetail = () => {
  const { slug } = useParams();
  const data = specializationData[slug];

  if (!data) {
    return <Navigate to="/specializations" replace />;
  }

  const IconComponent = data.icon;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <div 
        className="relative h-[300px] bg-cover bg-center"
        style={{ backgroundImage: `url(${data.image})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/90 via-[#1a365d]/60 to-transparent" />
        <div className="container-wide relative z-10 h-full flex flex-col justify-end pb-8">
          <div className="flex items-center gap-4 mb-2">
            <IconComponent className="h-10 w-10 text-white" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl md:text-4xl font-serif text-white">{data.title}</h1>
              <p className="text-xl font-serif text-white/80">{data.subtitle}</p>
            </div>
          </div>
          <div className="breadcrumb mt-2">
            <Link to="/">Home</Link> &gt; <Link to="/specializations">Areas of Specialization</Link> &gt; {data.title}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-12" data-testid="specialization-detail-content">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            
            {/* Main Column */}
            <div className="lg:col-span-2">
              <p className="text-lg text-[var(--text-medium)] mb-8 leading-relaxed">
                {data.description}
              </p>

              <h2 className="text-2xl font-serif text-[#1a365d] mb-4">Coverage Options</h2>
              <ul className="space-y-3 mb-8">
                {data.coverageTypes.map((coverage, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--gold)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-dark)]">{coverage}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-2xl font-serif text-[#1a365d] mb-4">Why Choose Archon Insurance?</h2>
              <ul className="space-y-3 mb-8">
                {data.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--gold)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-dark)]">{benefit}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-2xl font-serif text-[#1a365d] mb-4">Industries We Serve</h2>
              <ul className="space-y-3">
                {data.industries.map((industry, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--gold)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-dark)]">{industry}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-[var(--cream)] p-6 rounded sticky top-24">
                <h3 className="text-xl font-serif text-[#1a365d] mb-4">Ready to Get Started?</h3>
                <p className="text-[var(--text-medium)] text-sm mb-6">
                  Our team is ready to help you find the right coverage for your business. 
                  Contact us today for a personalized quote.
                </p>
                <Link 
                  to={`/contact-business-insurance?specialization=${encodeURIComponent(data.title + ' - ' + data.subtitle)}`}
                  className="btn-navy w-full text-center block mb-4"
                  data-testid="detail-contact-btn"
                >
                  Request a Quote
                </Link>
                <Link 
                  to="/specializations"
                  className="btn-outline-navy w-full text-center block"
                  data-testid="back-to-specializations"
                >
                  Back to All Areas
                </Link>

                <div className="mt-8 pt-6 border-t border-[#d4c9b5]">
                  <h4 className="font-semibold text-[#1a365d] mb-2">Contact Us Directly</h4>
                  <p className="text-sm text-[var(--text-medium)]">
                    <a href="mailto:monte@archoninsurance.com" className="text-[var(--gold)] hover:underline">
                      monte@archoninsurance.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SpecializationDetail;
